import Axios from 'axios'
import { getErrorMessage, HAZARD_URL } from '@/main'
import { cloneDeep } from 'lodash-es'
import { showErrorToast } from '@/utils/toast'

const state = {
  reportData: null,
  sortBy: [],
  addedFilters: [],
  defaultHazardOptions: {
    minSimilarity: 0.95,
    minNumber: null,
    maxNumber: null,
    fingerprintType: 'bingo',
    similarityType: 'tanimoto',
    authority: null,
    hazardName: null,
  },
  hazardOptions: {
    cts: null,
    minSimilarity: 0.95,
    minNumber: null,
    maxNumber: null,
    fingerprintType: 'bingo',
    similarityType: 'tanimoto',
    analogsSearchType: null,
    authority: null,
    hazardName: null,
  },
  hazardConfirmDateTime: null,
  hazardReportFilters: {
    sourcesFilter: [],
    testsFilter: [],
    profile: null,
  },

  loading: false,
  errorMessage: null,

  hazardProfiles: [],
  hazardCategories: [],
  hazardEndpoints: [],
  hazardLevels: [],
  hazardSourceTypes: [],
  hazardTestTypes: [],

  hazardSources: [],
  hazardRoutes: [],
  hazardCats: [],
  hazardCodes: [],
  hazardOrganisms: [],
}

const getters = {
  getHazardReport: (state) => {
    return state.reportData
  },
  getHazardLoading: (state) => state.loading,
  getHazardErrorMessage: (state) => state.errorMessage,
  getHazardSortBy: (state) => {
    return state.sortBy
  },
  getAddedFilters: (state) => {
    return state.addedFilters
  },
  getHazardOptions: (state) => {
    return state.hazardOptions
  },
  getHazardConfirmDateTime: (state) => {
    return state.hazardConfirmDateTime
  },
  getDefaultHazardOptions: (state) => {
    return state.defaultHazardOptions
  },
  getHazardReportFilters: (state) => {
    return state.hazardReportFilters
  },
  getHazardProfiles: (state) => {
    return state.hazardProfiles
  },
  getHazardCategories: (state) => {
    return state.hazardCategories
  },
  getHazardCategoriesHierarchy(state, getters) {
    let root = []
    let all = []
    for (let node of getters.getHazardCategories.concat(getters.getHazardEndpoints)) {
      node = cloneDeep(node)
      all.push(node)
      if (!node.parentId) root.push(node)
      else {
        let parent = all.find((c) => c.id === node.parentId)
        if (!('children' in parent)) parent.children = []
        parent.children.push(node)
      }
    }
    return root
  },
  getHazardEndpoints: (state) => {
    return state.hazardEndpoints
  },

  getHazardLevels: (state) => {
    return state.hazardLevels
  },
  getHazardSourceTypes: (state) => {
    return state.hazardSourceTypes
  },
  getHazardTestTypes: (state) => {
    return state.hazardTestTypes
  },
  getHazardSources: (state) => {
    return state.hazardSources
  },
  getHazardRoutes: (state) => {
    return state.hazardRoutes
  },
  getHazardCats: (state) => {
    return state.hazardCats
  },
  getHazardCodes: (state) => {
    return state.hazardCodes
  },
  getHazardOrganisms: (state) => {
    return state.hazardOrganisms
  },
}

const mutations = {
  SET_HAZARD_REPORT: (state, payload) => {
    state.reportData = payload
  },
  RESET_HAZARD_REPORT: (state) => {
    state.reportData = null
    state.loading = false
    state.errorMessage = null
  },

  SET_HAZARD_LOADING: (state, payload) => {
    state.loading = payload
  },
  SET_HAZARD_ERROR_MESSAGE: (state, payload) => {
    state.errorMessage = payload
  },
  SET_HAZARD_SORT_BY: (state, payload) => {
    state.sortBy = payload
  },
  SET_ADDED_FILTERS: (state, payload) => {
    state.addedFilters = payload
  },
  SET_HAZARD_OPTIONS: (state, payload) => {
    state.hazardOptions = payload
  },
  SET_HAZARD_CONFIRM_DATE_TIME: (state, payload) => {
    state.hazardConfirmDateTime = payload
  },
  SET_HAZARD_REPORT_FILTERS: (state, payload) => {
    state.hazardReportFilters = payload
  },
  SET_HAZARD_PROFILES: (state, payload) => {
    state.hazardProfiles = payload
  },
  SET_HAZARD_CATEGORIES: (state, payload) => {
    state.hazardCategories = payload
  },
  SET_HAZARD_ENDPOINTS: (state, payload) => {
    state.hazardEndpoints = payload
  },
  SET_HAZARD_LEVELS: (state, payload) => {
    state.hazardLevels = payload
  },
  SET_HAZARD_SOURCE_TYPES: (state, payload) => {
    state.hazardSourceTypes = payload
  },
  SET_HAZARD_TEST_TYPES: (state, payload) => {
    state.hazardTestTypes = payload
  },
  SET_HAZARD_SOURCES: (state, payload) => {
    state.hazardSources = payload
  },
  SET_HAZARD_ROUTES: (state, payload) => {
    state.hazardRoutes = payload
  },
  SET_HAZARD_CATS: (state, payload) => {
    state.hazardCats = payload
  },
  SET_HAZARD_CODES: (state, payload) => {
    state.hazardCodes = payload
  },
  SET_HAZARD_ORGANISMS: (state, payload) => {
    state.hazardOrganisms = payload
  },
}

const actions = {
  async getHazardReport(context, { chemicals, options } = {}) {
    const { commit, getters } = context

    commit('SET_VIEW_STRUCTURES', false)

    const request = {
      chemicals: chemicals.map((c) => ({
        chemical: c,
        ...(c.properties?.similarity ? { similarity: c.properties.similarity } : {}),
      })),
      options: options,
    }

    commit('SET_HAZARD_LOADING', true)
    commit('SET_HAZARD_ERROR_MESSAGE', null)

    return this._vm.$axios
      .post(HAZARD_URL, request)
      .then((response) => {
        commit('SET_HAZARD_REPORT', response.data)
        return response.data
      })
      .catch((error) => {
        commit('SET_HAZARD_ERROR_MESSAGE', getErrorMessage(error))

        const errorResponse = error?.response
        const responseStatus = errorResponse?.status

        if (responseStatus === 500) {
          if (
            errorResponse?.data?.message?.includes(
              'Failed to receive a response from the EPA Chemical Transformation Simulator service',
            )
          ) {
            if (request.chemicals.some((chemical) => !chemical.smiles)) {
              showErrorToast('There are invalid structures in the request.')
              return
            }
          }
        }

        if (responseStatus === 504) {
          showErrorToast('The request timed out. Try reducing the number of structures.')
          return
        }
        showErrorToast(error)
      })
      .finally(() => {
        commit('SET_HAZARD_LOADING', false)
      })
  },
}

export function prepareHazardMetadata(context) {
  Axios.get(`${HAZARD_URL}/metadata`)
    .then((response) => {
      context.commit('SET_HAZARD_PROFILES', response.data.hazardProfiles)
      context.commit('SET_HAZARD_CATEGORIES', response.data.hazardCategories)
      context.commit('SET_HAZARD_ENDPOINTS', response.data.hazardEndpoints)
      context.commit('SET_HAZARD_LEVELS', response.data.hazardLevels)
      context.commit('SET_HAZARD_SOURCE_TYPES', response.data.hazardSourceTypes)
      context.commit('SET_HAZARD_TEST_TYPES', response.data.hazardTestTypes)

      if (
        context.getters.getHazardReportFilters.sourcesFilter == null ||
        !context.getters.getHazardReportFilters.sourcesFilter.length
      ) {
        let sources = []
        for (let list of response.data.hazardSourceTypes) {
          sources.push(...list.children.map((c) => c.id))
        }
        context.commit('SET_HAZARD_REPORT_FILTERS', {
          ...context.getters.getHazardReportFilters,
          ...{ sourcesFilter: sources },
        })
      }

      if (
        context.getters.getHazardReportFilters.testsFilter == null ||
        !context.getters.getHazardReportFilters.testsFilter.length
      ) {
        let testTypes = []
        for (let list of response.data.hazardTestTypes) {
          testTypes.push(...list.children.map((c) => c.id))
        }
        context.commit('SET_HAZARD_REPORT_FILTERS', {
          ...context.getters.getHazardReportFilters,
          ...{ testsFilter: testTypes },
        })
      }

      context.commit('setFeatures', { hazard: { category: 'module' } })
    })
    .catch((error) => {
      console.warn('HAZARD module is not available: %o', getErrorMessage(error))
      context.commit('removeFeature', { hazard: {} })
    })
}

export default {
  state,
  getters,
  mutations,
  actions,
}

export function hazardReportFilteredOrdered(report, filters, getSortBy, showUnlikely) {
  if (!report) return []
  function compareHazardScores(sort_by) {
    const scoreWeights = { VH: 4, H: 3, M: 2, L: 1 }
    const authorityWeights = {
      'Authoritative': 3,
      'Screening': 2,
      'QSAR Model': 1,
    }
    return function (a, b) {
      if (!sort_by[0]) return 0

      let cmp = 0
      for (const srt of sort_by) {
        let swA, swB, awA, awB
        if (srt[0] !== 'similarity') {
          const scoreA = a.scores.filter((s) => s.hazardId === srt[0])[0]
          const scoreB = b.scores.filter((s) => s.hazardId === srt[0])[0]

          swA = scoreWeights[scoreA?.finalScore] || 0
          swB = scoreWeights[scoreB?.finalScore] || 0

          awA = authorityWeights[scoreA?.finalAuthority] || 0
          awB = authorityWeights[scoreB?.finalAuthority] || 0
        } else {
          swA = a.requestChemical.properties.similarity
          swB = b.requestChemical.properties.similarity
          awA = awB = 3
        }

        cmp = swA - swB
        cmp = srt[1] ? cmp : cmp * -1
        if (cmp) break

        cmp = awA - awB
        cmp = srt[1] ? cmp : cmp * -1
        if (cmp) break
      }

      return cmp
    }
  }

  // Filter out UNLIKELY if any
  let filtered = report.hazardChemicals.filter(
    (r) =>
      showUnlikely ||
      !('ctsChemical' in r.requestChemical.properties) ||
      r.requestChemical.properties.ctsChemical.likelihood !== 'UNLIKELY',
  )

  if (filters[0]) {
    // "Include" filters
    filtered = filtered.filter((c) => {
      for (const f of filters) {
        for (const s of c.scores) {
          if ((!f[0] || s.hazardId === f[0]) && f[1].includes(s.finalScore)) return true
        }
      }
      return false
    })

    // "Exclude" filters
    filtered = filtered.filter((c) => {
      for (const f of filters) {
        for (const s of c.scores) {
          if ((!f[0] || s.hazardId === f[0]) && f[2].includes(s.finalScore)) return false
        }
      }
      return true
    })
  }

  return filtered.sort(compareHazardScores(getSortBy))
}
